import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, CheckCircle, Warning, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// Paste-from-Excel bulk import. Excel/Sheets copy as tab-separated text;
// we auto-split columns, guess the mapping, and let the admin adjust it.
export const ImportModal = ({ title, endpoint, fields, noun, hint, onClose, onImported }) => {
  const [text, setText] = useState("");
  const [hasHeader, setHasHeader] = useState(false);
  const [mapping, setMapping] = useState({}); // colIdx -> fieldKey
  const [status, setStatus] = useState("inactive");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
    if (!lines.length) return { rows: [], cols: 0 };
    const delim = lines.some((l) => l.includes("\t")) ? "\t" : ",";
    const rows = lines.map((l) => l.split(delim).map((c) => c.trim()));
    const cols = Math.max(...rows.map((r) => r.length));
    return { rows, cols };
  }, [text]);

  useEffect(() => {
    if (!parsed.rows.length) { setMapping({}); setHasHeader(false); return; }
    const first = parsed.rows[0];
    const guess = (cell) => {
      const s = (cell || "").toLowerCase().trim();
      if (!s) return "";
      let best = "", bestLen = 0;
      for (const f of fields) {
        for (const kw of f.match) {
          if (s.includes(kw) && kw.length > bestLen) { best = f.key; bestLen = kw.length; }
        }
      }
      return best;
    };
    const headerGuesses = first.map(guess);
    const looksHeader = headerGuesses.filter(Boolean).length >= 2 && !first.some((c) => (c || "").includes("@"));
    const m = {};
    if (looksHeader) {
      const used = new Set();
      headerGuesses.forEach((g, i) => { if (g && !used.has(g)) { m[i] = g; used.add(g); } });
    } else {
      m[0] = fields[0].key;
      const emailField = fields.find((f) => f.key.includes("email"));
      const phoneField = fields.find((f) => f.key.includes("phone"));
      const taxField = fields.find((f) => f.key === "tax_id");
      for (let i = 1; i < parsed.cols; i++) {
        const cells = parsed.rows.map((r) => r[i] || "").filter(Boolean);
        if (!cells.length) continue;
        const emailish = cells.filter((c) => c.includes("@")).length / cells.length;
        const phoneish = cells.filter((c) => c.replace(/\D/g, "").length >= 7 && c.replace(/[\d\s\-().+]/g, "") === "").length / cells.length;
        const taxish = cells.filter((c) => /^\d{3}-\d{2}-\d{4}$/.test(c) || /^\d{2}-\d{7}$/.test(c)).length / cells.length;
        if (emailField && emailish > 0.5 && !Object.values(m).includes(emailField.key)) m[i] = emailField.key;
        else if (taxField && taxish > 0.5 && !Object.values(m).includes(taxField.key)) m[i] = taxField.key;
        else if (phoneField && phoneish > 0.5 && !Object.values(m).includes(phoneField.key)) m[i] = phoneField.key;
      }
    }
    setHasHeader(looksHeader);
    setMapping(m);
  }, [parsed, fields]);

  const setCol = (i, key) => {
    setMapping((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (next[k] === key) delete next[k]; });
      if (key) next[i] = key; else delete next[i];
      return next;
    });
  };

  const dataRows = hasHeader ? parsed.rows.slice(1) : parsed.rows;

  const submit = async () => {
    if (!Object.values(mapping).includes(fields[0].key)) {
      toast.error(`Map a column to "${fields[0].label}" first.`);
      return;
    }
    const rows = dataRows
      .map((r) => {
        const o = {};
        Object.entries(mapping).forEach(([i, k]) => { const v = (r[i] || "").trim(); if (v) o[k] = v; });
        return o;
      })
      .filter((o) => Object.keys(o).length > 0);
    if (!rows.length) { toast.error("Nothing to import — paste some rows first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { rows, status });
      setResult(data);
      if (data.created > 0) onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="import-modal">
      <div className="bg-white rounded-md w-full max-w-3xl p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button type="button" data-testid="import-close-btn" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>

        {result ? (
          <div data-testid="import-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
              <CheckCircle size={22} weight="fill" className="text-emerald-600 flex-shrink-0" />
              <div>
                <div className="font-black text-lg" data-testid="import-created-count">
                  {result.created} {noun}{result.created === 1 ? "" : "s"} imported
                </div>
                {result.skipped.length > 0 && <div className="text-sm text-slate-600 mt-0.5">{result.skipped.length} row{result.skipped.length === 1 ? "" : "s"} skipped (details below).</div>}
              </div>
            </div>
            {result.skipped.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Skipped rows</div>
                <div className="border border-amber-200 bg-amber-50 rounded-md divide-y divide-amber-100 max-h-48 overflow-y-auto" data-testid="import-skipped-list">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="px-3 py-2 text-sm flex gap-2">
                      <span className="font-semibold">{s.name}</span>
                      <span className="text-slate-600">— {s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.warnings?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Warnings (rows still imported)</div>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {result.warnings.map((w, i) => (
                    <div key={i} className="px-3 py-2 text-sm text-slate-600 flex items-start gap-2">
                      <Warning size={14} className="text-amber-500 mt-0.5 flex-shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="import-done-btn" onClick={onClose}
              className="mt-5 w-full h-11 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-3">
              Copy rows straight from your Excel list and paste them below. Columns split automatically —
              then confirm which column is which before importing. Blanks are fine; duplicates are skipped.
            </p>
            {hint && <p className="text-xs text-slate-500 -mt-2 mb-3">{hint}</p>}
            <textarea
              data-testid="import-paste-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder={"Paste here — e.g.\nJohn Smith\tjohn@example.com\t740-555-1234\nJane Doe\t\t614-555-9876"}
              className="w-full p-3 border border-slate-300 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
            />

            {parsed.rows.length > 0 && (
              <>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="text-sm flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="import-has-header" checked={hasHeader}
                      onChange={(e) => setHasHeader(e.target.checked)} className="rounded" />
                    First row is a header
                  </label>
                  <div className="text-sm flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Import as</span>
                    {["inactive", "active"].map((s) => (
                      <label key={s} className="flex items-center gap-1 cursor-pointer capitalize">
                        <input type="radio" name="import-status" data-testid={`import-status-${s}`}
                          checked={status === s} onChange={() => setStatus(s)} />
                        {s}
                      </label>
                    ))}
                  </div>
                  <div className="text-xs text-slate-500">{dataRows.length} row{dataRows.length === 1 ? "" : "s"} detected</div>
                </div>

                <div className="mt-3 border border-slate-200 rounded-md overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        {Array.from({ length: parsed.cols }, (_, i) => (
                          <th key={i} className="px-2 py-2 text-left">
                            <select
                              data-testid={`import-col-select-${i}`}
                              value={mapping[i] || ""}
                              onChange={(e) => setCol(i, e.target.value)}
                              className={`h-8 px-1 border rounded-md bg-white w-full min-w-[110px] font-semibold ${mapping[i] ? "border-green-400 text-green-800" : "border-slate-300 text-slate-400"}`}
                            >
                              <option value="">— ignore —</option>
                              {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                            </select>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dataRows.slice(0, 8).map((r, ri) => (
                        <tr key={ri} className="border-t border-slate-100">
                          {Array.from({ length: parsed.cols }, (_, ci) => (
                            <td key={ci} className={`px-2 py-1.5 ${mapping[ci] ? "text-slate-800" : "text-slate-400"}`}>{r[ci] || ""}</td>
                          ))}
                        </tr>
                      ))}
                      {dataRows.length > 8 && (
                        <tr className="border-t border-slate-100">
                          <td colSpan={parsed.cols} className="px-2 py-1.5 text-slate-400 italic">… and {dataRows.length - 8} more row{dataRows.length - 8 === 1 ? "" : "s"}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">Cancel</button>
              <button
                data-testid="import-submit-btn"
                onClick={submit}
                disabled={busy || dataRows.length === 0}
                className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UploadSimple size={18} weight="bold" />
                {busy ? "Importing…" : `Import ${dataRows.length} ${noun}${dataRows.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportModal;
